/**
 * Locked accounts — super admin only.
 *
 * Lists every account currently locked out for repeated content
 * guideline violations (see CONTENT_VIOLATION_LOCKOUT_THRESHOLD in
 * @jemmi/schemas). Deliberately a separate page from AdminDashboardPage
 * rather than another tab there: this is every locked user's email in
 * one place, which is more sensitive than day-to-day content moderation
 * and shouldn't be routine reading for every admin.
 *
 * Guarded by RequireSuperAdmin (see App.tsx) — a mock, client-side check
 * against the current test session (see mockSession.ts), not a real
 * security boundary. TODO: Phase 2 — replace with real server-side
 * authorization once auth exists; a client-side gate can be bypassed by
 * anyone inspecting/editing browser state.
 */

import { useState } from 'react';
import { ShieldAlert, Lock, LockOpen } from 'lucide-react';
import { CONTENT_VIOLATION_LOCKOUT_THRESHOLD } from '@jemmi/schemas';

import { mockLockedAccounts, type LockedAccountSummary } from '@/lib/mockData';

export function LockedAccountsPage() {
  const [accounts, setAccounts] = useState<LockedAccountSummary[]>(mockLockedAccounts);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const handleUnlock = (id: string) => {
    // TODO: Phase 2 - POST /api/admin/users/:id/unlock
    // UPDATE users SET isLocked = false WHERE id = ?
    // This should be logged (who unlocked, when, why) since the product
    // decision is that lockouts are permanent by default — an unlock is
    // a deliberate manual override, not routine.
    console.log('[admin] Unlocking account', id);
    setAccounts((prev) => prev.filter((a) => a.id !== id));
    setConfirmingId(null);
  };

  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <ShieldAlert className="h-6 w-6 text-red-600" />
        <h1 className="text-3xl font-bold text-slate-900">Locked accounts</h1>
      </div>
      <p className="mb-1 text-slate-600">
        Accounts are locked automatically after {CONTENT_VIOLATION_LOCKOUT_THRESHOLD} submissions
        are removed for violating content guidelines. Lockouts are permanent by default — only
        unlock an account if you've reviewed the case and it warrants an exception.
      </p>
      <p className="mb-6 text-sm font-medium text-red-700">Super admin access only.</p>

      {accounts.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <p className="text-slate-500">No locked accounts.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold text-slate-900">User</th>
                  <th className="px-6 py-3 text-left font-semibold text-slate-900">Email</th>
                  <th className="px-6 py-3 text-left font-semibold text-slate-900">Violations</th>
                  <th className="px-6 py-3 text-left font-semibold text-slate-900">Locked</th>
                  <th className="px-6 py-3 text-left font-semibold text-slate-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr
                    key={account.id}
                    className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 flex-shrink-0 text-red-500" />
                        <span className="font-medium text-slate-900">{account.displayName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{account.email}</td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                        {account.violationCount} of {CONTENT_VIOLATION_LOCKOUT_THRESHOLD}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {new Date(account.lockedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {confirmingId === account.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600">Unlock this account?</span>
                          <button
                            type="button"
                            onClick={() => handleUnlock(account.id)}
                            className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-red-700"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingId(null)}
                            className="text-xs font-medium text-slate-500 hover:text-slate-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingId(account.id)}
                          className="text-brand-600 hover:text-brand-700 flex items-center gap-1.5 text-xs font-semibold"
                        >
                          <LockOpen className="h-3.5 w-3.5" />
                          Unlock
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
